var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
var _a;
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { useCallback, useContext, useEffect, useMemo, useRef, useState, } from 'react';
import globalStore from '../store';
import '../styles/widgets/spinnerManipulative.scss';
import { DataContext } from '../player/DataContext';
import customEventHandler from '../helper/customEvents';
import { playAudio } from '../helper/audioHelper';
import { saveState } from '../helper/stateManagerHelper';
import { registerMultiSelectSaveEntries } from '../helper/multiSelectSaveCoordinator';
import { announce } from '../helper/announcer';
import { selectTool } from '../helper/helper';
import ButtonComponent from '../components/ButtonComponent';
import { countVisibleCharsInLatex } from '../helper/latexCharCount';
import MathEditor from '../components/MathEditor';
import { useTranslation } from 'react-i18next';
import { useDuplicateAnnounce, useCreationAnnounce } from '../helper/hooks/useDuplicateAnnounce';
import { getImageSrc } from '../helper/imageBlobCache';
import { useMultiSelectDragSource } from '../helper/hooks/useMultiSelectDrag';
var IS_TOUCH_DEVICE = typeof window !== 'undefined' && !!((_a = window.matchMedia) === null || _a === void 0 ? void 0 : _a.call(window, '(pointer: coarse)').matches);
var SPIN_DURATION = 3000; // ms
var DEG_PER_PERCENT = 360 / 100;
var MIN_SECTION_PERCENT = 1;
var DOT_HIT_RADIUS = 15;
var DRAG_SENSITIVITY = 1;
/** Convert raw LaTeX to screen-reader-friendly text */
function latexToReadable(latex) {
    if (!latex)
        return '';
    var work = latex.replace(/^\\\(/, '').replace(/\\\)$/, '').trim();
    // Replace LaTeX commands with readable equivalents
    // Fractions: \frac{a}{b} or \frac ab → "a over b"
    work = work.replace(/\\frac\{([^}]*)\}\{([^}]*)\}/g, '$1 over $2');
    work = work.replace(/\\frac(\d)(\d)/g, '$1 over $2');
    // Exponents: x^{y} or x^y → "x to the power of y"
    work = work.replace(/\^\{([^}]*)\}/g, ' to the power of $1');
    work = work.replace(/\^(\d)/g, ' to the power of $1');
    // Operators
    work = work.replace(/\\times/g, 'times');
    work = work.replace(/\\div/g, 'divided by');
    work = work.replace(/\\cdot/g, 'times');
    work = work.replace(/\\pm/g, 'plus or minus');
    // Comparisons
    work = work.replace(/\\le(?:q)?/g, 'less than or equal to');
    work = work.replace(/\\ge(?:q)?/g, 'greater than or equal to');
    work = work.replace(/\\lt/g, 'less than');
    work = work.replace(/\\gt/g, 'greater than');
    work = work.replace(/\\ne(?:q)?/g, 'not equal to');
    // Symbols
    work = work.replace(/\\%/g, 'percent');
    work = work.replace(/\\\\$/g, 'dollars');
    work = work.replace(/\\pi/g, 'pi');
    work = work.replace(/\\sqrt\{([^}]*)\}/g, 'square root of $1');
    // Spacing and placeholders
    work = work.replace(/\\;/g, ' ');
    work = work.replace(/\\,/g, ' ');
    work = work.replace(/\\placeholder\{[^}]*\}/g, '');
    // Clean up remaining backslashes and braces
    work = work.replace(/[{}]/g, '');
    work = work.replace(/\\/g, '');
    work = work.replace(/\s+/g, ' ').trim();
    return work;
}
/** Extract readable text from section text (string or MathEditor array format) */
function extractReadableText(text) {
    if (typeof text === 'string')
        return latexToReadable(text);
    if (!Array.isArray(text))
        return '';
    return text
        .map(function (item) {
        if (item.type === 'math')
            return latexToReadable(item.latex || '');
        return item.value || '';
    })
        .join(' ')
        .trim();
}
/** Find the section index the arrow currently points to */
function getCurrentSectionIndex(rotation, startAngle, sections) {
    var normalizedAngle = ((rotation % 360) + 360) % 360;
    // Should be:
    // const pointerAngleDeg = (90 - startAngle + 360) % 360;
    // const effectiveAngle = (pointerAngleDeg + normalizedAngle + 36000) % 360;// Should be:
    var pointerAngleDeg = (90 - startAngle + 360) % 360;
    var effectiveAngle = (pointerAngleDeg + normalizedAngle + 36000) % 360;
    var cumDeg = 0;
    for (var i = 0; i < sections.length; i++) {
        cumDeg += sections[i].percent * DEG_PER_PERCENT;
        if (effectiveAngle < cumDeg)
            return i;
    }
    return 0;
}
/* ------------------------------------------------------------------ */
/*  SVG geometry helpers                                               */
/* ------------------------------------------------------------------ */
var degToRad = function (deg) { return (deg * Math.PI) / 180; };
/**
 * Build an SVG arc-slice path from centre (cx, cy) with given radius.
 * startDeg / endDeg are in degrees (0 = right, -90 = top).
 */
var buildArcPath = function (cx, cy, radius, startDeg, endDeg) {
    var span = endDeg - startDeg;
    if (span >= 359.99) {
        // Full circle – use two half-arcs
        var r = radius;
        return ["M ".concat(cx, " ").concat(cy - r), "A ".concat(r, " ").concat(r, " 0 1 1 ").concat(cx - 0.001, " ").concat(cy - r), "Z"].join(' ');
    }
    var x1 = cx + radius * Math.cos(degToRad(startDeg));
    var y1 = cy + radius * Math.sin(degToRad(startDeg));
    var x2 = cx + radius * Math.cos(degToRad(endDeg));
    var y2 = cy + radius * Math.sin(degToRad(endDeg));
    var largeArc = span > 180 ? 1 : 0;
    return [
        "M ".concat(cx, " ").concat(cy),
        "L ".concat(x1, " ").concat(y1),
        "A ".concat(radius, " ").concat(radius, " 0 ").concat(largeArc, " 1 ").concat(x2, " ").concat(y2),
        "Z",
    ].join(' ');
};
/** Position for a label at the mid-angle of a section, 60 % out from centre. */
var labelPosition = function (cx, cy, radius, startDeg, endDeg) {
    var midDeg = (startDeg + endDeg) / 2;
    var dist = radius * 0.6;
    return {
        x: cx + dist * Math.cos(degToRad(midDeg)),
        y: cy + dist * Math.sin(degToRad(midDeg)),
    };
};
/**
 * Count visible characters in a section's text content.
 * Matches the Cards manipulative approach: font = chordWidth / charCount.
 * Empty math boxes (□) count as 3 characters to account for their visual width.
 */
var countSectionChars = function (text) {
    if (typeof text === 'string')
        return text.length;
    if (!Array.isArray(text))
        return 0;
    return text.reduce(function (total, item) {
        if (typeof item === 'string')
            return total + item.length;
        if (item.type === 'math') {
            var latex = item.latex || '';
            // Empty latex renders as a □ placeholder box — count as 3 chars
            if (latex.trim().length === 0)
                return total + 3;
            return total + countVisibleCharsInLatex(latex);
        }
        if (item.value) {
            var clean = item.value.replace(/<[^>]*>/g, '').trim();
            return total + clean.length;
        }
        return total;
    }, 0);
};
var SpinnerManipulative = function (props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w, _x, _y, _z, _0;
    var id = props.id, data = props.data, _1 = props.showOperator, showOperator = _1 === void 0 ? false : _1, calledFrom = props.calledFrom, setData = props.setData;
    var isWidget = calledFrom === 'widget';
    var t = useTranslation().t;
    var duplicateLabel = useDuplicateAnnounce(id, t('text.manipulativeSpinner'));
    var creationLabel = useCreationAnnounce(id, t('text.manipulativeSpinner'));
    /* ---- stores ---- */
    var _2 = globalStore.useStoreState(function (s) { return s.playerStore; }), zoomLevel = _2.zoomLevel, isIDM = _2.isIDM, isSwp = _2.isSwp;
    var _3 = globalStore.useStoreState(function (s) { return s.jsonDataStore; }), mainConfig = _3.mainConfig, bottomToolConfig = _3.bottomToolConfig;
    /* ---- context ---- */
    var _4 = useContext(DataContext), selectedTool = _4.selectedTool, setSelectedTool = _4.setSelectedTool, setManipulative = _4.setManipulative, manipulative = _4.manipulative, setActiveManipulator = _4.setActiveManipulator, setManiplulatorComponentVisible = _4.setManiplulatorComponentVisible, setSelectedManipulativeId = _4.setSelectedManipulativeId, panningEnable = _4.panningEnable, setContextMenu = _4.setContextMenu;
    /* ---- derived props ---- */
    // IDM spinners default to a transparent disc so the page's PDF/background
    // shows through. Non-IDM keeps the original solid-white fallback.
    var defaultSectionColor = (isIDM || isSwp) ? 'transparent' : '#ffffff';
    var sections = (_a = data.sections) !== null && _a !== void 0 ? _a : [{ text: '', color: defaultSectionColor, percent: 100 }];
    // The source JSON's `radius` describes the inner circle (where the colored
    // sections end). The artwork's ring border occupies an additional fraction
    // of the wheel — `sectionRadius = outerRadius * SECTION_FRACTION` — so we
    // scale the outer radius up by 1 / SECTION_FRACTION to wrap the border
    // around the requested inner radius.
    var SECTION_FRACTION = 0.92;
    var radius = ((_b = data.radius) !== null && _b !== void 0 ? _b : 160) / SECTION_FRACTION;
    var startAngle = (_c = data.sectionStartAngle) !== null && _c !== void 0 ? _c : -90;
    var arrowOnly = (_d = data.arrowOnly) !== null && _d !== void 0 ? _d : false;
    var positionLock = (_f = (_e = data.lock) === null || _e === void 0 ? void 0 : _e.positioning) !== null && _f !== void 0 ? _f : false;
    var center = (_g = data.center) !== null && _g !== void 0 ? _g : { x: 0, y: 0 };
    var scaleVal = (_h = data.scale) !== null && _h !== void 0 ? _h : 1;
    // Reliable touch-primary detection — works on modern iPads (desktop UA)
    var isTouchPrimary = typeof window !== 'undefined' && !!((_j = window.matchMedia) === null || _j === void 0 ? void 0 : _j.call(window, '(pointer: coarse)').matches);
    // Track whether focus came from a screen reader (VoiceOver sets :focus-visible on focus)
    var lastFocusWasScreenReader = useRef(false);
    /* ---- state ---- */
    var _5 = useState(false), isFocused = _5[0], setIsFocused = _5[1];
    var _6 = useState(false), isSpinning = _6[0], setIsSpinning = _6[1];
    var _7 = useState((_k = data.rotation) !== null && _k !== void 0 ? _k : 0), rotation = _7[0], setRotation = _7[1];
    var _8 = useState(center), spinnerPosition = _8[0], setSpinnerPosition = _8[1];
    var _9 = useState(scaleVal), spinnerScale = _9[0], setSpinnerScale = _9[1];
    var _10 = useState(''), announceText = _10[0], setAnnounceText = _10[1];
    /* ---- refs ---- */
    var wrapperRef = useRef(null);
    var positionDragRef = useRef(null);
    var _11 = useState(false), isDraggingSelf = _11[0], setIsDraggingSelf = _11[1];
    var _12 = useState(null), dotMenu = _12[0], setDotMenu = _12[1];
    var dotMenuRef = useRef(null);
    // Tracks whether a real pointer (mouse/touch) initiated the current click.
    // JAWS/VoiceOver synthetic clicks never fire pointerdown/mousedown first,
    // so we use this to detect screen-reader activation across all platforms.
    var dotPointerDownRef = useRef(false);
    var dotAnnounceTimer = useRef(null);
    var keyMoveStartRef = useRef(null);
    var keyMoveCurPosRef = useRef(null);
    var keyMoveSaveTimer = useRef(null);
    /* ---- section-drag refs (popup only) ---- */
    var wheelRef = useRef(null);
    var isDraggingRef = useRef(false);
    var dragIndexRef = useRef(null);
    var lastAngleRef = useRef(null);
    var dragCenterRef = useRef(null);
    var lastStateSyncRef = useRef(0);
    var dragSnapshotRef = useRef(null);
    var handleDragMoveRef = useRef(function () { });
    var handleDragEndRef = useRef(function () { });
    /* ---- computed dimensions ---- */
    var diameter = radius * 2;
    var wrapperSize = diameter * spinnerScale;
    var sectionRadius = radius * 0.92; // radius of pie sections (inner edge of ring)
    var dotRadius = radius * 0.9; // radius for drag dots (inside the ring border)
    // Needle (pointer) sized by its LENGTH as a fraction of the rendered wheel
    // radius, so its tip reaches the same way toward the sections at any wheel
    // size. The old `39 * spinnerScale` width was tied only to the scale factor,
    // not the radius, so on a large-radius wheel (e.g. a 608px spinner) the needle
    // stayed tiny and looked far too short. The pointer image keeps its intrinsic
    // aspect ratio (we set height and let width auto-follow). 0.8 = the tip
    // reaches 80% of the radius, sitting just inside the colored ring.
    var POINTER_LENGTH_RATIO = 0.8;
    var pointerLength = (wrapperSize / 2) * POINTER_LENGTH_RATIO;
    /* ---- sync from props ---- */
    useEffect(function () {
        if (isWidget)
            setSpinnerPosition(center);
    }, [center.x, center.y, isWidget]);
    useEffect(function () { return setSpinnerScale(scaleVal); }, [scaleVal]);
    var prevSelectedToolRef = useRef([]);
    useEffect(function () {
        var wasFocused = prevSelectedToolRef.current.includes(id);
        var nowFocused = selectedTool.includes(id);
        prevSelectedToolRef.current = selectedTool;
        setIsFocused(nowFocused);
        // Auto-focus the spinner when newly selected (e.g. after popup Done)
        if (nowFocused && !wasFocused && isWidget && wrapperRef.current) {
            setTimeout(function () {
                var _a, _b;
                // Don't steal focus from the context menu
                if ((_a = document.activeElement) === null || _a === void 0 ? void 0 : _a.closest('.shape-context-menu'))
                    return;
                (_b = wrapperRef.current) === null || _b === void 0 ? void 0 : _b.focus();
            }, 100);
        }
    }, [selectedTool, id]);
    useEffect(function () {
        var _a;
        setRotation((_a = data.rotation) !== null && _a !== void 0 ? _a : 0);
    }, [data.rotation]);
    /* ================================================================== */
    /*  POSITION DRAG  (workspace only, via ButtonComponent overlay)      */
    /* ================================================================== */
    /** Extract client coordinates from mouse or touch events */
    var getClientPoint = function (e) {
        if ('touches' in e && e.touches.length > 0) {
            return { x: e.touches[0].clientX, y: e.touches[0].clientY };
        }
        if ('changedTouches' in e && e.changedTouches.length > 0) {
            return { x: e.changedTouches[0].clientX, y: e.changedTouches[0].clientY };
        }
        return { x: e.clientX, y: e.clientY };
    };
    var _13 = useMultiSelectDragSource(id, selectedTool), startMultiDrag = _13.startMultiDrag, moveMultiDrag = _13.moveMultiDrag, endMultiDrag = _13.endMultiDrag;
    var onPositionDragMove = function (e) {
        var _a, _b;
        if (!positionDragRef.current || positionLock || data.isPreset)
            return;
        if ('touches' in e)
            e.preventDefault();
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var _d = positionDragRef.current, startPos = _d.startPos, offset = _d.offset;
        // Only start moving after a 5px threshold (distinguishes tap from drag)
        if (!positionDragRef.current.hasMoved) {
            var dx = x - startPos.x;
            var dy = y - startPos.y;
            if (Math.sqrt(dx * dx + dy * dy) > 5) {
                positionDragRef.current.hasMoved = true;
            }
            else {
                return;
            }
        }
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        // Only update local position during drag for visual feedback.
        // Context (setManipulative) is synced once on drop in onPositionDragEnd.
        var newX = (x - parentRect.left) / zoomLevel + offset.x;
        var newY = (y - parentRect.top) / zoomLevel + offset.y;
        setSpinnerPosition({ x: newX, y: newY });
        var initialCenter = positionDragRef.current.initialCenter;
        if (initialCenter) {
            moveMultiDrag(newX - initialCenter.x, newY - initialCenter.y);
        }
    };
    var onPositionDragEnd = function (e) {
        var _a, _b;
        if (!positionDragRef.current)
            return;
        setIsDraggingSelf(false);
        if (!positionDragRef.current.hasMoved) {
            // Tap (no drag) — select the spinner (spin only from the spin button)
            if (!isFocused) {
                setSelectedTool(selectTool(e, id, selectedTool, false));
            }
        }
        else {
            // Drag ended — save position for undo/redo
            var _c = getClientPoint(e), x = _c.x, y = _c.y;
            var _d = positionDragRef.current, offset = _d.offset, initialCenter = _d.initialCenter;
            var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
            if (parentRect && initialCenter) {
                var newX_1 = (x - parentRect.left) / zoomLevel + offset.x;
                var newY_1 = (y - parentRect.top) / zoomLevel + offset.y;
                var entry = {
                    type: id,
                    id: id.split('_')[1],
                    propertyName: 'center',
                    old: JSON.stringify(initialCenter),
                    new: JSON.stringify({ x: newX_1, y: newY_1 }),
                };
                if (!registerMultiSelectSaveEntries(id, [entry])) {
                    saveState(entry);
                }
                // Sync final position and move spinner to front of render order
                setManipulative(function (prev) {
                    var index = prev.findIndex(function (item) { return item.data.id === id; });
                    if (index === -1)
                        return prev;
                    var updated = __spreadArray([], prev, true);
                    var movedItem = updated.splice(index, 1)[0];
                    updated.push(__assign(__assign({}, movedItem), { data: __assign(__assign({}, movedItem.data), { center: { x: newX_1, y: newY_1 } }) }));
                    return updated;
                });
            }
        }
        positionDragRef.current = null;
        window.removeEventListener('mousemove', onPositionDragMove);
        window.removeEventListener('touchmove', onPositionDragMove);
        window.removeEventListener('mouseup', onPositionDragEnd);
        window.removeEventListener('touchend', onPositionDragEnd);
        endMultiDrag();
    };
    var onPositionDragStart = function (e) {
        var _a, _b;
        if (!isWidget || panningEnable)
            return;
        e.preventDefault();
        e.stopPropagation();
        setIsDraggingSelf(true);
        var foundManip = manipulative.find(function (m) { return m.data.id === id; });
        var _c = getClientPoint(e), x = _c.x, y = _c.y;
        var parentRect = (_b = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.parentElement) === null || _b === void 0 ? void 0 : _b.getBoundingClientRect();
        if (!parentRect)
            return;
        var mouseXInParent = (x - parentRect.left) / zoomLevel;
        var mouseYInParent = (y - parentRect.top) / zoomLevel;
        positionDragRef.current = {
            startPos: { x: x, y: y },
            offset: {
                x: center.x - mouseXInParent,
                y: center.y - mouseYInParent,
            },
            hasMoved: false,
            initialCenter: foundManip
                ? JSON.parse(JSON.stringify(foundManip.data.center))
                : __assign({}, center),
        };
        window.addEventListener('mousemove', onPositionDragMove);
        window.addEventListener('touchmove', onPositionDragMove, { passive: false });
        window.addEventListener('mouseup', onPositionDragEnd);
        window.addEventListener('touchend', onPositionDragEnd);
        startMultiDrag();
    };
    /* ================================================================== */
    /*  SPIN ANIMATION                                                    */
    /* ================================================================== */
    var handleSpin = function () {
        if (isSpinning || !isWidget)
            return;
        setIsSpinning(true);
        playAudio('spinner');
        var extraSpins = (3 + Math.random() * 3) * 360; // 3-6 full turns
        var randomOffset = Math.random() * 360;
        var newRotation = rotation + extraSpins + randomOffset;
        setRotation(newRotation);
        // Save state for undo/redo
        var oldRotation = rotation;
        setManipulative(function (prev) {
            return prev.map(function (item) {
                return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { rotation: newRotation }) }) : item;
            });
        });
        var normalizedAngle = ((newRotation % 360) + 360) % 360;
        // Arrow points down at rotation=0 (180° offset from section start at top/-90°)
        var effectiveAngle = (90 - startAngle + normalizedAngle + 36000) % 360;
        var cumDeg = 0;
        // DEBUG: remove after fixing
        for (var i = 0; i < sections.length; i++) {
            cumDeg += sections[i].percent * DEG_PER_PERCENT;
            if (effectiveAngle < cumDeg) {
                var readable = extractReadableText(sections[i].text);
                var sectionText = readable ? ": ".concat(readable) : '';
                setAnnounceText(t('text.spinnerLandedOn', { section: i + 1, label: sectionText }));
                break;
            }
        }
        saveState({
            type: id,
            id: id.split('_')[1],
            propertyName: 'rotation',
            old: JSON.stringify(oldRotation),
            new: JSON.stringify(newRotation),
        });
        setTimeout(function () {
            setIsSpinning(false);
            // Announce which section the arrow landed on
            // const normalizedAngle = ((newRotation % 360) + 360) % 360;
            // // Arrow points up (top = -90°), sections start at startAngle (default -90°)
            // const pointerAngleDeg = (-90 - startAngle + 360) % 360;
            // const effectiveAngle = (pointerAngleDeg - normalizedAngle + 36000) % 360;
            // let cumDeg = 0;
            // for (let i = 0; i < sections.length; i++) {
            //   cumDeg += sections[i].percent * DEG_PER_PERCENT;
            //   if (effectiveAngle < cumDeg) {
            //     const readable = extractReadableText(sections[i].text);
            //     const sectionText = readable ? `: ${readable}` : '';
            //     setAnnounceText(t('text.spinnerLandedOn', { section: i + 1, label: sectionText }));
            //     break;
            //   }
            // }
        }, SPIN_DURATION);
    };
    /* ================================================================== */
    /*  SECTION BOUNDARY DRAG (popup only)                                */
    /* ================================================================== */
    /** Extract pointer coordinates from mouse or touch events */
    var getPointerCoordinates = function (e) {
        if ('touches' in e) {
            if (e.touches.length > 0) {
                return { clientX: e.touches[0].clientX, clientY: e.touches[0].clientY };
            }
            else if (e.changedTouches && e.changedTouches.length > 0) {
                return { clientX: e.changedTouches[0].clientX, clientY: e.changedTouches[0].clientY };
            }
            return null;
        }
        return { clientX: e.clientX, clientY: e.clientY };
    };
    /** Compute raw angle (degrees) from cached center */
    var getAngleFromCache = function (e) {
        var c = dragCenterRef.current;
        if (!c)
            return null;
        var coords = getPointerCoordinates(e);
        if (!coords)
            return null;
        return Math.atan2(coords.clientY - c.y, coords.clientX - c.x) * (180 / Math.PI);
    };
    /** Stable wrappers whose identity never changes (safe for addEventListener) */
    var stableDragMove = useCallback(function (e) { return handleDragMoveRef.current(e); }, []);
    var stableDragEnd = useCallback(function () { return handleDragEndRef.current(); }, []);
    /** Recalculate all SVG paths + dot positions from snapshot and write to DOM directly */
    var updateDOMFromSnapshot = function () {
        var snap = dragSnapshotRef.current;
        if (!snap || !wheelRef.current || !wrapperRef.current)
            return;
        var svgPaths = wheelRef.current.querySelectorAll('.spinnerSection');
        var angleDeg = snap.startAngle;
        for (var i = 0; i < snap.sections.length; i++) {
            var pct = snap.sections[i].percent;
            var spanDeg = pct * DEG_PER_PERCENT;
            var endDeg = angleDeg + spanDeg;
            if (svgPaths[i]) {
                svgPaths[i].setAttribute('d', buildArcPath(cx, cy, sectionRadius, angleDeg, endDeg));
            }
            var dot = wrapperRef.current.querySelector("#dot-btn-".concat(i));
            if (dot) {
                var endRad = degToRad(endDeg);
                var dotX = cx + dotRadius * Math.cos(endRad);
                var dotY = cy + dotRadius * Math.sin(endRad);
                dot.style.left = "".concat((dotX / diameter) * wrapperSize - DOT_HIT_RADIUS, "px");
                dot.style.top = "".concat((dotY / diameter) * wrapperSize - DOT_HIT_RADIUS, "px");
                // Update ::before line angle — line rotates with the button
                dot.style.setProperty('--line-angle', "".concat(endDeg, "deg"));
            }
            angleDeg = endDeg;
        }
    };
    /** Drag-move handler — reassigned every render to capture latest values */
    handleDragMoveRef.current = function (e) {
        if (!isDraggingRef.current || dragIndexRef.current === null || lastAngleRef.current === null)
            return;
        if ('touches' in e)
            e.preventDefault();
        var currentAngle = getAngleFromCache(e);
        if (currentAngle === null)
            return;
        var angleDelta = currentAngle - lastAngleRef.current;
        if (angleDelta > 180)
            angleDelta -= 360;
        else if (angleDelta < -180)
            angleDelta += 360;
        var percentageDelta = (angleDelta / 360) * 100 * DRAG_SENSITIVITY;
        if (Math.abs(percentageDelta) < 0.05)
            return;
        lastAngleRef.current = currentAngle;
        var snap = dragSnapshotRef.current;
        if (!snap)
            return;
        var idx = dragIndexRef.current;
        var nextIdx = (idx + 1) % snap.sections.length;
        var isLastDot = idx === snap.sections.length - 1;
        var total = snap.sections[idx].percent + snap.sections[nextIdx].percent;
        var newCurrent = snap.sections[idx].percent + percentageDelta;
        var newNext = snap.sections[nextIdx].percent - percentageDelta;
        if (newCurrent < MIN_SECTION_PERCENT) {
            newCurrent = MIN_SECTION_PERCENT;
            newNext = total - MIN_SECTION_PERCENT;
        }
        else if (newNext < MIN_SECTION_PERCENT) {
            newNext = MIN_SECTION_PERCENT;
            newCurrent = total - MIN_SECTION_PERCENT;
        }
        if (isLastDot) {
            var nextDelta = newNext - snap.sections[nextIdx].percent;
            snap.startAngle -= (nextDelta / 100) * 360;
        }
        snap.sections[idx].percent = newCurrent;
        snap.sections[nextIdx].percent = newNext;
        updateDOMFromSnapshot();
        // Throttled React state sync (~20fps) so popup inputs update live
        var now = Date.now();
        if (now - lastStateSyncRef.current > 50) {
            lastStateSyncRef.current = now;
            if (setData) {
                setData(function (prev) {
                    var _a;
                    return (__assign(__assign({}, prev), { sectionStartAngle: snap.startAngle, sections: ((_a = prev.sections) !== null && _a !== void 0 ? _a : []).map(function (s, i) {
                            var _a, _b;
                            return (__assign(__assign({}, s), { percent: (_b = (_a = snap.sections[i]) === null || _a === void 0 ? void 0 : _a.percent) !== null && _b !== void 0 ? _b : s.percent }));
                        }) }));
                });
            }
        }
    };
    /** Drag-end handler — commits final snapshot to React state */
    handleDragEndRef.current = function () {
        var snap = dragSnapshotRef.current;
        if (snap && setData) {
            setData(function (prev) {
                var _a;
                return (__assign(__assign({}, prev), { sectionStartAngle: snap.startAngle, sections: ((_a = prev.sections) !== null && _a !== void 0 ? _a : []).map(function (s, i) {
                        var _a, _b;
                        return (__assign(__assign({}, s), { percent: (_b = (_a = snap.sections[i]) === null || _a === void 0 ? void 0 : _a.percent) !== null && _b !== void 0 ? _b : s.percent }));
                    }) }));
            });
        }
        isDraggingRef.current = false;
        dragIndexRef.current = null;
        lastAngleRef.current = null;
        dragCenterRef.current = null;
        dragSnapshotRef.current = null;
        window.removeEventListener('mousemove', stableDragMove);
        window.removeEventListener('mouseup', stableDragEnd);
        window.removeEventListener('touchmove', stableDragMove);
        window.removeEventListener('touchend', stableDragEnd);
        window.removeEventListener('touchcancel', stableDragEnd);
    };
    /** Start drag — compute center from click position + geometry (Safari-safe) */
    var startDrag = function (e, dotIndex) {
        var _a, _b;
        if (!showOperator || !setData)
            return;
        e.preventDefault();
        e.stopPropagation();
        // Blur any focused input to commit pending percent changes
        if (document.activeElement instanceof HTMLElement) {
            document.activeElement.blur();
        }
        var rawClickX = 'clientX' in e ? e.clientX : e.touches[0].clientX;
        var rawClickY = 'clientY' in e ? e.clientY : e.touches[0].clientY;
        lastStateSyncRef.current = 0;
        // Snap to exact dot center using offsetX/offsetY
        var ne = e.nativeEvent;
        var dotCenterX = typeof ne.offsetX === 'number' ? rawClickX - (ne.offsetX - DOT_HIT_RADIUS) : rawClickX;
        var dotCenterY = typeof ne.offsetY === 'number' ? rawClickY - (ne.offsetY - DOT_HIT_RADIUS) : rawClickY;
        // Derive viewport scale from the dot button's rendered size
        var dotElement = e.currentTarget;
        var dotRect = dotElement.getBoundingClientRect();
        var vpScale = dotRect.width / (DOT_HIT_RADIUS * 2);
        isDraggingRef.current = true;
        dragIndexRef.current = dotIndex;
        // Read the latest committed state via setData updater to avoid stale snapshot
        // after blur committed a pending percent change
        var latestSections = sections;
        var latestStartAngle = startAngle;
        if (setData) {
            setData(function (prev) {
                var _a, _b;
                latestSections = (_a = prev.sections) !== null && _a !== void 0 ? _a : sections;
                latestStartAngle = (_b = prev.sectionStartAngle) !== null && _b !== void 0 ? _b : startAngle;
                return prev; // no-op, just reading
            });
        }
        // Known angle of this dot from section data
        var dotAngle = degToRad(latestStartAngle);
        for (var i = 0; i <= dotIndex; i++) {
            dotAngle += (((_b = (_a = latestSections[i]) === null || _a === void 0 ? void 0 : _a.percent) !== null && _b !== void 0 ? _b : 0) / 100) * 2 * Math.PI;
        }
        // Spinner center = dot center - dot's offset from spinner center (in viewport coords)
        dragCenterRef.current = {
            x: dotCenterX - dotRadius * Math.cos(dotAngle) * vpScale,
            y: dotCenterY - dotRadius * Math.sin(dotAngle) * vpScale,
        };
        // Deep-copy latest sections for drag snapshot
        dragSnapshotRef.current = {
            sections: latestSections.map(function (s) { return (__assign({}, s)); }),
            startAngle: latestStartAngle,
        };
        var nativeEvent = e.nativeEvent;
        lastAngleRef.current = getAngleFromCache(nativeEvent);
        window.addEventListener('mousemove', stableDragMove);
        window.addEventListener('mouseup', stableDragEnd);
        window.addEventListener('touchmove', stableDragMove, { passive: false });
        window.addEventListener('touchend', stableDragEnd);
        window.addEventListener('touchcancel', stableDragEnd);
    };
    /** Cleanup section drag listeners on unmount */
    useEffect(function () {
        return function () {
            window.removeEventListener('mousemove', stableDragMove);
            window.removeEventListener('mouseup', stableDragEnd);
            window.removeEventListener('touchmove', stableDragMove);
            window.removeEventListener('touchend', stableDragEnd);
            window.removeEventListener('touchcancel', stableDragEnd);
        };
    }, [stableDragMove, stableDragEnd]);
    /* ================================================================== */
    /*  BOTTOM PANEL EVENTS  (workspace only)                             */
    /* ================================================================== */
    useEffect(function () {
        if (!isWidget || !isFocused)
            return;
        var onToolClick = function (evt) {
            switch (evt.id) {
                case 'edit':
                    setActiveManipulator('spinners');
                    setManiplulatorComponentVisible(true);
                    setSelectedManipulativeId(id);
                    break;
                case 'scaleMinus':
                case 'scalePlus': {
                    // Read the current scale from the global manipulative array inside
                    // the setManipulative updater (single source of truth), compute the
                    // new scale there, but defer saveState until after setManipulative
                    // returns — StrictMode can double-invoke the updater, and a side
                    // effect fired from inside it would push a duplicate history entry.
                    var isPlus_1 = evt.id === 'scalePlus';
                    var _oldScale_1;
                    var _newScale_1;
                    setManipulative(function (prev) {
                        var next = prev.map(function (item) {
                            var _a;
                            if (item.data.type === 'spinner' && item.data.id === id) {
                                _oldScale_1 = (_a = item.data.scale) !== null && _a !== void 0 ? _a : 1;
                                _newScale_1 = isPlus_1 ? _oldScale_1 + 0.1 : Math.max(0.5, _oldScale_1 - 0.1);
                                return __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { scale: _newScale_1 }) });
                            }
                            return item;
                        });
                        return next;
                    });
                    if (_oldScale_1 !== undefined && _newScale_1 !== undefined && _oldScale_1 !== _newScale_1) {
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'scale',
                            old: JSON.stringify(_oldScale_1),
                            new: JSON.stringify(_newScale_1),
                        });
                    }
                    if (_newScale_1 !== undefined) {
                        setSpinnerScale(_newScale_1);
                        announce(t('text.manipulativeScaled', {
                            name: t('text.spinner'),
                            direction: isPlus_1 ? t('text.up') : t('text.down'),
                            value: _newScale_1.toFixed(1),
                        }));
                    }
                    break;
                }
            }
        };
        customEventHandler.addEventListener('bottomPanel', onToolClick);
        var onSpinWheel = function (data) {
            if (data.id === id && !isSpinning) {
                handleSpin();
            }
        };
        customEventHandler.addEventListener('spinWheel', onSpinWheel);
        return function () {
            customEventHandler.removeEventListener('bottomPanel', onToolClick);
            customEventHandler.removeEventListener('spinWheel', onSpinWheel);
        };
    }, [isWidget, isFocused, spinnerScale, id, manipulative, isSpinning]);
    /* ================================================================== */
    /*  RENDER: SVG sections + labels                                     */
    /* ================================================================== */
    var cx = radius;
    var cy = radius;
    /* ---- Font-size helpers (use module-level spinnerContentEm / spinnerLatexEm) ---- */
    /** Returns how many times taller than font-size this content renders. */
    var getContentHeightFactor = function (text) {
        if (!Array.isArray(text))
            return 1.1;
        var factor = 1.1;
        for (var _i = 0, text_1 = text; _i < text_1.length; _i++) {
            var item = text_1[_i];
            if (item.type === 'math' && item.latex) {
                var l = item.latex;
                if (/\\frac|\\int|\\sum|\\prod/.test(l))
                    factor = Math.max(factor, 2.2);
                else if (/\\sqrt|[\^_]/.test(l))
                    factor = Math.max(factor, 1.5);
                else
                    factor = Math.max(factor, 1.2);
            }
        }
        return factor;
    };
    /** Compute ideal font size (px) for a single section. */
    var computeSectionFontSize = function (sectionPercent, text) {
        var arcAngleRad = (sectionPercent / 100) * 2 * Math.PI;
        var radiusFactor = sectionPercent < 15 ? 0.65 : 0.45 + 0.2 * (1 - sectionPercent / 100);
        var textRadius = radius * radiusFactor;
        // Single section (100%): use radius + radius/2 as the full available width
        // For other ≥50% sections: factor grows 0.65 (at 50%) → 0.85 (approaching 100%)
        var largeSectionFactor = 0.65 + 0.2 * ((sectionPercent - 50) / 50);
        var chordWidth = sectionPercent === 100
            ? radius * 1.5
            : sectionPercent >= 50
                ? textRadius * 2 * largeSectionFactor
                : 2 * textRadius * Math.sin(arcAngleRad / 2) * 0.9;
        // Count visible characters — font = chordWidth / charCount (Cards approach)
        var charCount = countSectionChars(text);
        var heightFactor = getContentHeightFactor(text);
        var availableHeight = radius * 0.35;
        var fontFromWidth = charCount > 0 ? chordWidth / charCount : availableHeight;
        var fontFromHeight = availableHeight / heightFactor;
        var minFont = 10;
        var maxFont = Math.max(25, radius * 0.22);
        return Math.round(Math.max(minFont, Math.min(fontFromWidth, fontFromHeight, maxFont)));
    };
    /** Get text label position with variable radius factor based on section size. */
    var getTextPosition = function (sectionPercent, midAngleRad) {
        var minRadiusFactor = 0.45;
        var maxRadiusFactor = 0.65;
        var radiusFactor = sectionPercent < 15
            ? maxRadiusFactor
            : minRadiusFactor + (maxRadiusFactor - minRadiusFactor) * (1 - sectionPercent / 100);
        var textRadius = radius * radiusFactor;
        return {
            x: radius + textRadius * Math.cos(midAngleRad),
            y: radius + textRadius * Math.sin(midAngleRad),
        };
    };
    /** Get chord width at the text position for maxWidth constraint. */
    var getChordWidth = function (sectionPercent) {
        var arcAngleRad = (sectionPercent / 100) * 2 * Math.PI;
        var minRadiusFactor = 0.45;
        var maxRadiusFactor = 0.65;
        var radiusFactor = sectionPercent < 15
            ? maxRadiusFactor
            : minRadiusFactor + (maxRadiusFactor - minRadiusFactor) * (1 - sectionPercent / 100);
        var textRadius = radius * radiusFactor;
        var largeSectionFactor = 0.65 + 0.2 * ((sectionPercent - 50) / 50);
        return sectionPercent === 100
            ? radius * 1.5
            : sectionPercent >= 50
                ? textRadius * 2 * largeSectionFactor
                : 2 * textRadius * Math.sin(arcAngleRad / 2) * 0.9;
    };
    /** Uniform font size: smallest computed size across all sections with text, scaled by spinnerScale. */
    var uniformFontSize = useMemo(function () {
        if (arrowOnly || !sections.length)
            return 14 * spinnerScale;
        var sizes = sections
            .filter(function (sec) { return countSectionChars(sec.text) > 0; })
            .map(function (sec) { return computeSectionFontSize(sec.percent, sec.text); });
        if (sizes.length === 0)
            return 14 * spinnerScale;
        return Math.min.apply(Math, sizes) * spinnerScale;
    }, [sections, radius, arrowOnly, spinnerScale]);
    var sectionPaths = useMemo(function () {
        if (arrowOnly || !sections.length)
            return [];
        var currentDeg = startAngle;
        return sections.map(function (sec, i) {
            var span = sec.percent * DEG_PER_PERCENT;
            var path = buildArcPath(cx, cy, sectionRadius, currentDeg, currentDeg + span);
            var lbl = labelPosition(cx, cy, sectionRadius, currentDeg, currentDeg + span);
            var result = {
                path: path,
                color: sec.color,
                label: lbl,
                startDeg: currentDeg,
                endDeg: currentDeg + span,
                index: i,
            };
            currentDeg += span;
            return result;
        });
    }, [sections, startAngle, radius, arrowOnly, cx, cy]);
    /** Dot positions for section-boundary drag (popup mode). */
    var dotPositions = useMemo(function () {
        if (!showOperator || arrowOnly || sections.length <= 1)
            return [];
        var currentDeg = startAngle;
        return sections.map(function (sec, i) {
            currentDeg += sec.percent * DEG_PER_PERCENT;
            return {
                x: cx + dotRadius * Math.cos(degToRad(currentDeg)),
                y: cy + dotRadius * Math.sin(degToRad(currentDeg)),
                index: i,
            };
        });
    }, [sections, startAngle, radius, showOperator, arrowOnly, cx, cy]);
    /* ================================================================== */
    /*  JSX                                                                */
    /* ================================================================== */
    var spinnerCfg = mainConfig === null || mainConfig === void 0 ? void 0 : mainConfig.spinnerManipulative;
    var spinnerLayerSrc = (_o = (_m = (_l = spinnerCfg === null || spinnerCfg === void 0 ? void 0 : spinnerCfg.spinner) === null || _l === void 0 ? void 0 : _l.imgSrc) === null || _m === void 0 ? void 0 : _m.normal) !== null && _o !== void 0 ? _o : './assets/images/manipulative/spinner/spinnerLayer.svg';
    var pointerSrc = (_r = (_q = (_p = spinnerCfg === null || spinnerCfg === void 0 ? void 0 : spinnerCfg.pointer) === null || _p === void 0 ? void 0 : _p.imgSrc) === null || _q === void 0 ? void 0 : _q.normal) !== null && _r !== void 0 ? _r : './assets/images/manipulative/spinner/pointer.svg';
    var reloadSrc = (_t = (_s = spinnerCfg === null || spinnerCfg === void 0 ? void 0 : spinnerCfg.reload) === null || _s === void 0 ? void 0 : _s.imgSrc) !== null && _t !== void 0 ? _t : {
        normal: './assets/images/manipulative/spinner/reloadN.svg',
        hover: './assets/images/manipulative/spinner/reloadH.svg',
    };
    // Move a dot boundary by a given number of degrees (positive = clockwise)
    var moveDotBoundary = useCallback(function (dotIndex, degrees) {
        if (!setData)
            return;
        var percentDelta = (degrees / 360) * 100;
        var announcedFrom = 0;
        var announcedTo = 0;
        var nextIdxForAnnounce = 0;
        setData(function (prev) {
            var _a, _b, _c, _d, _e;
            var secs = ((_a = prev.sections) !== null && _a !== void 0 ? _a : []).map(function (s) { return (__assign({}, s)); });
            if (secs.length < 2)
                return prev;
            var nextIdx = (dotIndex + 1) % secs.length;
            nextIdxForAnnounce = nextIdx;
            var isLastDot = dotIndex === secs.length - 1;
            var total = secs[dotIndex].percent + secs[nextIdx].percent;
            var newCurrent = secs[dotIndex].percent + percentDelta;
            var newNext = secs[nextIdx].percent - percentDelta;
            if (newCurrent < MIN_SECTION_PERCENT) {
                newCurrent = MIN_SECTION_PERCENT;
                newNext = total - MIN_SECTION_PERCENT;
            }
            else if (newNext < MIN_SECTION_PERCENT) {
                newNext = MIN_SECTION_PERCENT;
                newCurrent = total - MIN_SECTION_PERCENT;
            }
            secs[dotIndex].percent = Math.round(newCurrent * 100) / 100;
            secs[nextIdx].percent = Math.round(newNext * 100) / 100;
            announcedFrom = Math.round(secs[dotIndex].percent * 10) / 10;
            announcedTo = Math.round(secs[nextIdx].percent * 10) / 10;
            var newStartAngle = (_b = prev.sectionStartAngle) !== null && _b !== void 0 ? _b : -90;
            if (isLastDot) {
                var nextDelta = secs[nextIdx].percent - ((_e = (_d = (_c = prev.sections) === null || _c === void 0 ? void 0 : _c[nextIdx]) === null || _d === void 0 ? void 0 : _d.percent) !== null && _e !== void 0 ? _e : 0);
                newStartAngle -= (nextDelta / 100) * 360;
            }
            return __assign(__assign({}, prev), { sections: secs, sectionStartAngle: newStartAngle });
        });
        // Debounce: only announce the latest value after user stops pressing arrow keys
        if (dotAnnounceTimer.current)
            clearTimeout(dotAnnounceTimer.current);
        dotAnnounceTimer.current = setTimeout(function () {
            var msg = t('text.boundaryMoved', {
                from: dotIndex + 1,
                fromPercent: announcedFrom,
                to: nextIdxForAnnounce + 1,
                toPercent: announcedTo,
            });
            // Fire both announcers:
            // - announce(): global live region for the widget-on-canvas case
            // - setAnnounceText: in-component live region, which lives INSIDE
            //   the Spinner popup's modal dialog and so still speaks when the
            //   popup's aria-modal="true" makes the document-root announcer
            //   inert to JAWS / VoiceOver.
            announce(msg);
            setAnnounceText('');
            requestAnimationFrame(function () { return setAnnounceText(msg); });
        }, 300);
    }, [setData]);
    // Close dot context menu on click outside
    useEffect(function () {
        if (!dotMenu)
            return;
        var handleMouseDown = function (e) {
            if (dotMenuRef.current && !dotMenuRef.current.contains(e.target)) {
                setDotMenu(null);
            }
        };
        document.addEventListener('mousedown', handleMouseDown, true);
        return function () { return document.removeEventListener('mousedown', handleMouseDown, true); };
    }, [dotMenu]);
    return (_jsxs("div", { ref: wrapperRef, className: "spinnerManipulativeWrapper", style: {
            position: isWidget ? 'absolute' : 'relative',
            // Offset by half the *rendered* size so the wheel stays centered on
            // its center point at any scale (wrapperSize already encodes the
            // scale; using the unscaled radius here shifted scaled wheels).
            top: isWidget ? spinnerPosition.y - wrapperSize / 2 : 0,
            left: isWidget ? spinnerPosition.x - wrapperSize / 2 : 0,
            width: wrapperSize,
            height: wrapperSize,
            zIndex: isDraggingSelf ? 999 : undefined,
            // ['--scaleSpinner']: `${spinnerScale}`,
            // No transform scale: `wrapperSize` (and the per-element `* spinnerScale`
            // factors) already apply the scale once. The previous widget-only
            // `scale(data.scale)` applied it a second time, so the on-board spinner
            // rendered scale² too big relative to the popup (which used scale(1)).
            transformOrigin: 'center center',
        }, tabIndex: isWidget ? 0 : -1, role: isWidget ? 'application' : 'group', "aria-label": (_u = duplicateLabel !== null && duplicateLabel !== void 0 ? duplicateLabel : creationLabel) !== null && _u !== void 0 ? _u : (function () {
            var _a;
            if (arrowOnly)
                return t('text.spinnerArrowOnlyAriaLabel');
            var base = t('text.spinnerAriaLabel', { count: sections.length });
            var idx = getCurrentSectionIndex(rotation, startAngle, sections);
            var readable = extractReadableText((_a = sections[idx]) === null || _a === void 0 ? void 0 : _a.text);
            var current = readable
                ? t('text.spinnerLandedOn', { section: idx + 1, label: ": ".concat(readable) })
                : t('text.spinnerLandedOn', { section: idx + 1, label: '' });
            return "".concat(base, " ").concat(current);
        })(), "aria-roledescription": "spinner manipulative", "data-MFDeselect": "true", "data-manipulative-id": id, onFocus: function (e) {
            var _a, _b;
            try {
                lastFocusWasScreenReader.current = !!((_b = (_a = e.target).matches) === null || _b === void 0 ? void 0 : _b.call(_a, ':focus-visible'));
            }
            catch (_c) {
                lastFocusWasScreenReader.current = false;
            }
        }, onBlur: function () {
            lastFocusWasScreenReader.current = false;
        }, onClick: function (e) {
            var _a;
            if (!isWidget || panningEnable)
                return;
            // On touch devices, VoiceOver sets :focus-visible before synthesizing click.
            // Regular touch does NOT set :focus-visible. Desktop uses onKeyDown instead.
            if (isTouchPrimary && lastFocusWasScreenReader.current) {
                e.preventDefault();
                lastFocusWasScreenReader.current = false;
                if (!selectedTool.includes("".concat(id))) {
                    setSelectedTool(["".concat(id)]);
                }
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect) {
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: id,
                    });
                }
            }
        }, onKeyDown: function (e) {
            var _a, _b;
            if (!isWidget || panningEnable)
                return;
            // Shift+F10 or ContextMenu key → open context menu
            if (e.key === 'ContextMenu' ||
                (e.shiftKey && e.key === 'F10') ||
                e.key === 'Enter' ||
                e.key === ' ') {
                e.preventDefault();
                // Ensure spinner is selected so delete/duplicate/edit work
                if (!selectedTool.includes("".concat(id))) {
                    setSelectedTool(["".concat(id)]);
                }
                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                if (rect) {
                    setContextMenu({
                        x: rect.right,
                        y: rect.top,
                        manipulativeId: id,
                    });
                }
                return;
            }
            // if (!isSpinning) {
            //   e.preventDefault();
            //   handleSpin();
            //   return;
            // }
            // Arrow key movement
            if (['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                e.preventDefault();
                e.stopPropagation();
                if (positionLock || data.isPreset)
                    return;
                var curPos = (_b = keyMoveCurPosRef.current) !== null && _b !== void 0 ? _b : spinnerPosition;
                if (!keyMoveStartRef.current) {
                    keyMoveStartRef.current = __assign({}, curPos);
                }
                var step = e.shiftKey ? 20 : 5;
                var scaledStep = step / zoomLevel;
                var delta = {
                    ArrowUp: { x: 0, y: -scaledStep },
                    ArrowDown: { x: 0, y: scaledStep },
                    ArrowLeft: { x: -scaledStep, y: 0 },
                    ArrowRight: { x: scaledStep, y: 0 },
                };
                var d = delta[e.key];
                var newCenter_1 = { x: curPos.x + d.x, y: curPos.y + d.y };
                keyMoveCurPosRef.current = newCenter_1;
                setSpinnerPosition(newCenter_1);
                setManipulative(function (prev) {
                    return prev.map(function (item) {
                        return item.data.id === id ? __assign(__assign({}, item), { data: __assign(__assign({}, item.data), { center: newCenter_1 }) }) : item;
                    });
                });
                // Debounced undo/redo save + screen reader announcement
                if (keyMoveSaveTimer.current)
                    clearTimeout(keyMoveSaveTimer.current);
                keyMoveSaveTimer.current = setTimeout(function () {
                    if (keyMoveStartRef.current && keyMoveCurPosRef.current) {
                        var oldPos = keyMoveStartRef.current;
                        var newPos_1 = keyMoveCurPosRef.current;
                        saveState({
                            type: id,
                            id: id.split('_')[1],
                            propertyName: 'center',
                            old: JSON.stringify(oldPos),
                            new: JSON.stringify(newPos_1),
                        });
                        var dx = Math.round(newPos_1.x - oldPos.x);
                        var dy = Math.round(newPos_1.y - oldPos.y);
                        var directions_1 = [];
                        if (dy < 0)
                            directions_1.push("".concat(t('text.up'), " ").concat(Math.abs(dy)));
                        if (dy > 0)
                            directions_1.push("".concat(t('text.down'), " ").concat(dy));
                        if (dx < 0)
                            directions_1.push("".concat(t('text.left'), " ").concat(Math.abs(dx)));
                        if (dx > 0)
                            directions_1.push("".concat(t('text.right'), " ").concat(dx));
                        setAnnounceText('');
                        requestAnimationFrame(function () {
                            setAnnounceText(t('text.manipulativeMoved', {
                                name: t('text.spinner'),
                                direction: directions_1.join(" ".concat(t('text.and'), " ")),
                                x: Math.round(newPos_1.x),
                                y: Math.round(newPos_1.y),
                            }));
                        });
                        keyMoveStartRef.current = null;
                        keyMoveCurPosRef.current = null;
                    }
                }, 500);
            }
        }, children: [_jsxs("div", { className: "spinnerContainer", children: [!arrowOnly && (_jsxs("svg", { ref: wheelRef, className: "spinnerSvg", viewBox: "0 0 ".concat(diameter, " ").concat(diameter), xmlns: "http://www.w3.org/2000/svg", role: "img", "aria-label": "", "aria-hidden": "true", children: [_jsx("title", { children: "Spinner sections" }), _jsx("desc", { children: "Spinner wheel with sections" }), sectionPaths.map(function (sec, i) { return (_jsx("path", { d: sec.path, fill: sec.color, className: "spinnerSection", stroke: !showOperator && sections.length > 1 ? '#293273' : 'none', strokeWidth: !showOperator && sections.length > 1 ? 2 : 0 }, i)); })] })), !arrowOnly &&
                        sectionPaths.map(function (sec, i) {
                            var _a;
                            var text = (_a = sections[i]) === null || _a === void 0 ? void 0 : _a.text;
                            if (!text || countSectionChars(text) === 0)
                                return null;
                            var midAngleRad = degToRad((sec.startDeg + sec.endDeg) / 2);
                            var pos = getTextPosition(sections[i].percent, midAngleRad);
                            var chordWidth = getChordWidth(sections[i].percent);
                            return (_jsx("div", { className: "spinnerTextLabel", "aria-hidden": "true", 
                                // {...(isWidget ? { inert: '' } : {})}
                                inert: isWidget ? true : true, style: {
                                    left: "".concat((pos.x / diameter) * 100, "%"),
                                    top: "".concat((pos.y / diameter) * 100, "%"),
                                    transform: 'translate(-50%, -50%)',
                                    fontSize: uniformFontSize,
                                    maxWidth: "".concat(chordWidth * spinnerScale, "px"),
                                    textOverflow: 'ellipsis',
                                }, children: _jsx(MathEditor, { data: {
                                        type: 'data',
                                        data: {
                                            text: text,
                                            color: '#000000',
                                            alignment: 'center',
                                            size: uniformFontSize,
                                        },
                                    }, className: "spinnerLabelEditor", isTextUpdate: false, idPreffix: "spinnerPreviewText", index: i, keypadButtons: bottomToolConfig.mathKeypad, bottomBtns: bottomToolConfig.mathKeypadBottom, disableTransform: true, isManipulative: true, viewonly: true }, "spinnerPreviewText-".concat(i)) }, "label-".concat(i)));
                        }), _jsx("img", { className: "spinnerBaseImage", src: getImageSrc(spinnerLayerSrc), draggable: false, "aria-hidden": "true", alt: "" }), _jsx(ButtonComponent, { defaultCSS: "openBottomPanelDefault ".concat(((_w = (_v = positionDragRef.current) === null || _v === void 0 ? void 0 : _v.hasMoved) !== null && _w !== void 0 ? _w : false) && 'movingObject', "  ").concat(isFocused && calledFrom === 'widget' ? 'focusedObject' : ''), normalCSS: "openBottomPanelDefault", onMouseDown: onPositionDragStart, onTouchStart: onPositionDragStart, disabled: calledFrom === 'popup', ariaLabel: isWidget ? t('text.selectAndDragSpinner') : undefined, ariaHidden: !isWidget, tabIndex: -1 }), _jsx("img", { className: "pointerImage ".concat(isSpinning ? 'spinning' : ''), src: getImageSrc(pointerSrc), draggable: false, "aria-hidden": "true", alt: "", style: {
                            // Drive the needle by length (height); width auto-follows the
                            // image's aspect ratio. Overrides the CSS `width: 35px`.
                            height: "".concat(pointerLength, "px"),
                            width: 'auto',
                            transform: "translate(-50%, 0) rotate(".concat(rotation, "deg)"),
                            transition: isSpinning
                                ? "transform ".concat(SPIN_DURATION, "ms cubic-bezier(0.17, 0.67, 0.12, 0.99)")
                                : 'none',
                        } }), _jsx(ButtonComponent, { imgSrc: reloadSrc, disabled: isSpinning || calledFrom === 'popup', ariaLabel: !isWidget ? undefined : isSpinning ? t('text.spinning') : t('text.pressToSpin'), ariaHidden: !isWidget, defaultCSS: "spinWheel ".concat(isSpinning ? 'spinWheel_disabled' : ''), normalCSS: "spinWheel", clickHandler: function (e) {
                            var _a;
                            e.stopPropagation();
                            // iPad VoiceOver double-tap dispatches click at the visual center
                            // of the focused element. The spin button sits at that center, so
                            // VO's click targets it instead of the wrapper — accidentally
                            // spinning instead of opening the context menu. Detect the
                            // synthetic VO activation (touch device + detail===0) and route
                            // to context menu instead.
                            if (isTouchPrimary && e.detail === 0 && isWidget) {
                                e.preventDefault();
                                if (!selectedTool.includes("".concat(id))) {
                                    setSelectedTool(["".concat(id)]);
                                }
                                var rect = (_a = wrapperRef.current) === null || _a === void 0 ? void 0 : _a.getBoundingClientRect();
                                if (rect) {
                                    setContextMenu({
                                        x: rect.right,
                                        y: rect.top,
                                        manipulativeId: id,
                                    });
                                }
                                return;
                            }
                            if (!isSpinning)
                                handleSpin();
                        }, tabIndex: -1 }), (isSpinning || calledFrom === 'popup') && (_jsx("div", { className: "tempDiv spinWheel", style: { cursor: 'default', opacity: 0 }, "aria-hidden": true })), showOperator && (_jsx("div", { role: "application", "aria-label": t('text.sectionBoundaries'), className: "dotDragbtnGroup", children: dotPositions.map(function (dot, i) {
                            var _a, _b, _c, _d, _e, _f;
                            // Line runs from button center back to spinner center
                            var lineLength = (dotRadius / diameter) * wrapperSize;
                            var endDeg = (_b = (_a = sectionPaths[i]) === null || _a === void 0 ? void 0 : _a.endDeg) !== null && _b !== void 0 ? _b : 0;
                            return (_jsx("button", { id: "dot-btn-".concat(dot.index), className: "dotDragbtn", type: "button", style: {
                                    left: "".concat((dot.x / diameter) * wrapperSize - DOT_HIT_RADIUS, "px"),
                                    top: "".concat((dot.y / diameter) * wrapperSize - DOT_HIT_RADIUS, "px"),
                                    width: "".concat(DOT_HIT_RADIUS * 2, "px"),
                                    height: "".concat(DOT_HIT_RADIUS * 2, "px"),
                                    '--line-length': "".concat(lineLength, "px"),
                                    '--line-angle': "".concat(endDeg, "deg"),
                                }, onPointerDown: function () {
                                    dotPointerDownRef.current = true;
                                }, onMouseDown: function (e) {
                                    dotPointerDownRef.current = true;
                                    startDrag(e, dot.index);
                                }, onTouchStart: function (e) {
                                    dotPointerDownRef.current = true;
                                    startDrag(e, dot.index);
                                }, onClick: function (e) {
                                    // No prior pointerdown/mousedown/touchstart means the click
                                    // is synthetic — fired by JAWS (Windows), VoiceOver (Mac),
                                    // or VoiceOver (iPad) when the user activates the button.
                                    // Real mouse / touch always sets dotPointerDownRef first.
                                    var isSynthetic = !dotPointerDownRef.current || e.detail === 0;
                                    dotPointerDownRef.current = false;
                                    if (isSynthetic) {
                                        e.preventDefault();
                                        setDotMenu({ dotIndex: dot.index });
                                        setTimeout(function () {
                                            var _a, _b;
                                            (_b = (_a = dotMenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([aria-disabled="true"])')) === null || _b === void 0 ? void 0 : _b.focus();
                                        }, 50);
                                    }
                                }, onKeyDown: function (e) {
                                    var key = e.key;
                                    if (key === 'Escape') {
                                        // Stop propagation so popup doesn't close — JAWS exits forms mode at its own level
                                        e.stopPropagation();
                                        return;
                                    }
                                    var isArrow = key === 'ArrowLeft' ||
                                        key === 'ArrowRight' ||
                                        key === 'ArrowUp' ||
                                        key === 'ArrowDown';
                                    if (isArrow) {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        var direction = key === 'ArrowRight' || key === 'ArrowUp' ? 1 : -1;
                                        var degrees = e.shiftKey ? 5 : 1;
                                        moveDotBoundary(dot.index, degrees * direction);
                                        return;
                                    }
                                    if (key === 'Enter' || key === ' ') {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setDotMenu({ dotIndex: dot.index });
                                        setTimeout(function () {
                                            var _a, _b;
                                            (_b = (_a = dotMenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelector('button:not([disabled]):not([aria-disabled="true"])')) === null || _b === void 0 ? void 0 : _b.focus();
                                        }, 50);
                                    }
                                }, "aria-label": t('text.resizeBoundaryWithPercent', {
                                    from: dot.index + 1,
                                    to: ((dot.index + 1) % sections.length) + 1,
                                    fromPercent: Math.round(((_d = (_c = sections[dot.index]) === null || _c === void 0 ? void 0 : _c.percent) !== null && _d !== void 0 ? _d : 0) * 10) / 10,
                                    toPercent: Math.round(((_f = (_e = sections[(dot.index + 1) % sections.length]) === null || _e === void 0 ? void 0 : _e.percent) !== null && _f !== void 0 ? _f : 0) * 10) /
                                        10,
                                }) }, "dot-".concat(i)));
                        }) }))] }), dotMenu && (_jsxs("div", { ref: dotMenuRef, className: "dotContextMenu", role: "menu", "aria-label": t('text.resizeBoundary', {
                    from: dotMenu.dotIndex + 1,
                    to: ((dotMenu.dotIndex + 1) % sections.length) + 1,
                }), onKeyDown: function (e) {
                    var _a, _b;
                    var key = e.key;
                    if (key === 'Escape') {
                        e.preventDefault();
                        e.stopPropagation();
                        var dotId_1 = "dot-btn-".concat(dotMenu.dotIndex);
                        setDotMenu(null);
                        setTimeout(function () { var _a; return (_a = document.getElementById(dotId_1)) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
                        return;
                    }
                    if (key === 'ArrowDown' || key === 'ArrowUp' || key === 'Tab') {
                        e.preventDefault();
                        e.stopPropagation();
                        var btns = Array.from((_b = (_a = dotMenuRef.current) === null || _a === void 0 ? void 0 : _a.querySelectorAll('button')) !== null && _b !== void 0 ? _b : []);
                        if (btns.length === 0)
                            return;
                        var direction = key === 'ArrowUp' || (key === 'Tab' && e.shiftKey) ? -1 : 1;
                        var cur = btns.indexOf(document.activeElement);
                        // Arrow keys land on every menu item, including disabled ones,
                        // so screen readers announce "Move Forward, dimmed" and the
                        // user understands the current availability. Activation is
                        // still blocked by the click/Enter guards on the button
                        // itself, so focusing a disabled item has no side-effects.
                        var idx = (cur + direction + btns.length) % btns.length;
                        btns[idx].focus();
                    }
                }, style: {
                    position: 'absolute',
                    left: "".concat((_y = (_x = document.getElementById("dot-btn-".concat(dotMenu.dotIndex))) === null || _x === void 0 ? void 0 : _x.offsetLeft) !== null && _y !== void 0 ? _y : 0, "px"),
                    top: "".concat(((_0 = (_z = document.getElementById("dot-btn-".concat(dotMenu.dotIndex))) === null || _z === void 0 ? void 0 : _z.offsetTop) !== null && _0 !== void 0 ? _0 : 0) +
                        DOT_HIT_RADIUS * 2, "px"),
                }, children: [_jsx("div", { className: "dotContextMenu__header", role: "heading", "aria-level": 2, children: t('text.resizeBoundary', {
                            from: dotMenu.dotIndex + 1,
                            to: ((dotMenu.dotIndex + 1) % sections.length) + 1,
                        }) }), (function () {
                        var _a, _b, _c, _d;
                        // Forward = this dot moves clockwise → current section grows,
                        //           next section shrinks. Disabled when next is already
                        //           at the minimum.
                        // Backward = opposite: current shrinks, next grows. Disabled when
                        //            current is at the minimum.
                        var curPct = (_b = (_a = sections[dotMenu.dotIndex]) === null || _a === void 0 ? void 0 : _a.percent) !== null && _b !== void 0 ? _b : 0;
                        var nextPct = (_d = (_c = sections[(dotMenu.dotIndex + 1) % sections.length]) === null || _c === void 0 ? void 0 : _c.percent) !== null && _d !== void 0 ? _d : 0;
                        var forwardDisabled = nextPct <= MIN_SECTION_PERCENT;
                        var backwardDisabled = curPct <= MIN_SECTION_PERCENT;
                        return (_jsxs(_Fragment, { children: [_jsx("button", { role: "menuitem", tabIndex: 0, "aria-disabled": forwardDisabled || undefined, onClick: function () { return !forwardDisabled && moveDotBoundary(dotMenu.dotIndex, 1); }, children: t('text.moveForward') }), _jsx("button", { role: "menuitem", tabIndex: -1, "aria-disabled": backwardDisabled || undefined, onClick: function () { return !backwardDisabled && moveDotBoundary(dotMenu.dotIndex, -1); }, children: t('text.moveBackward') })] }));
                    })(), _jsx("div", { className: "dotContextMenu__divider" }), _jsx("button", { role: "menuitem", tabIndex: -1, onClick: function () {
                            var dotId = "dot-btn-".concat(dotMenu.dotIndex);
                            setDotMenu(null);
                            setTimeout(function () { var _a; return (_a = document.getElementById(dotId)) === null || _a === void 0 ? void 0 : _a.focus(); }, 0);
                        }, children: t('text.exitMenu') })] })), _jsx("div", { "aria-live": "polite", "aria-atomic": "true", style: {
                    position: 'absolute',
                    width: '1px',
                    height: '1px',
                    padding: 0,
                    margin: '-1px',
                    overflow: 'hidden',
                    clip: 'rect(0, 0, 0, 0)',
                    whiteSpace: 'nowrap',
                    border: 0,
                }, children: announceText })] }));
};
export default SpinnerManipulative;
